/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from 'react';

// react-router components
import { NavLink } from 'react-router-dom';

// prop-types is a library for typechecking of props.
import PropTypes from 'prop-types';
import intl from 'react-intl-universal';

// @material-ui core components
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Icon from '@mui/material/Icon';
import MenuIcon from '@mui/icons-material/Menu';
import MDBox from 'components/MDBox';
import MDBadge from 'components/MDBadge';
import { doesUserHaveHistoryPrivilege } from 'utils/privilege';

// self developed api
import api from 'utils/api';
import localData from 'utils/localData';

// Custom styles for DashboardNavbar
import { navbar, navbarContainer, navbarRow, navbarIconButton } from './styles';

// Material Dashboard 2 PRO React context
import { useMaterialUIController } from 'context';
import { DOLPHIN_API } from 'shared/constants/apis';
import { USER_DATA } from 'shared/constants/users';
import MDTypography from 'components/MDTypography';
import Notifications from 'components/Notifications';
import {
  doesUserHaveKenshinHistoryPrivilege,
  doesUserHaveSeimitsuHistoryPrivilege,
} from 'utils/privilege';
import { ServiceType } from 'shared/constants/service';
import { Chip, Tooltip } from '@mui/material';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';

const COMPANY_NAME = 'I.W.G DOCloud';

function DashboardNavbar({
  absolute = false,
  light = false,
  isMini = false,
  brand,
  onOpenDrawer,
}) {
  // states
  const [controller] = useMaterialUIController();
  const { transparentNavbar, darkMode } = controller;
  const currentOrganization = localData.get(USER_DATA.CURRENT_ORGANIZATION);

  const semitsuPrivilege = doesUserHaveSeimitsuHistoryPrivilege();
  const kenshinPrivilege = doesUserHaveKenshinHistoryPrivilege();

  const notificationFilterOptions = {
    [ServiceType.SEIMITSU]: semitsuPrivilege,
    [ServiceType.KENSHIN]: kenshinPrivilege,
    [ServiceType.ALL]: true,
  };

  const [openNotificationMenu, setOpenNotificationMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsPreferences, setNotificationsPreferences] = useState({});
  const [numNewNotifications, setNumNewNotifications] = useState(0);
  const [refresh, setRefresh] = useState(0);

  // increment value of `refresh` per specified time period (default is 60 seconds)
  const PERIOD_MS = import.meta.env.VITE_NOTIFICATION_FETCH_PERIOD_MS || 60000;
  useEffect(() => {
    const interval = setInterval(() => {
      if (import.meta.env.VITE_AUTH_SERVER === 'aws-cognito') {
        setRefresh((refresh) => refresh + 1);
      }
    }, PERIOD_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const apiVariables = {
      params: {
        userId: localData.get(USER_DATA.USER_ID),
        organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
      },
    };

    api.get(DOLPHIN_API.NOTIFICATIONS, apiVariables).then(async (data) => {
      const notifications = data.notifications;

      //! Removing all the notifications that the user does not have the privilege to see
      const notificationsAfterPrivilegeFilter = notifications.filter(
        (item) =>
          (semitsuPrivilege && item.service === ServiceType.SEIMITSU) ||
          (kenshinPrivilege && item.service === ServiceType.KENSHIN)
      );

      setNotifications(notificationsAfterPrivilegeFilter);
      setNumNewNotifications(
        notificationsAfterPrivilegeFilter.filter(
          (notification) => notification.clicked === false
        ).length
      );
    });
  }, [refresh]);

  useEffect(() => {
    const fetchPreferences = async () => {
      const response = await api.get(DOLPHIN_API.NOTIFICATION_PREFERENCE_ALL, {
        params: {
          userId: localData.get(USER_DATA.USER_ID),
          organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
        },
      });
      setNotificationsPreferences(response.preferences);
    };
    fetchPreferences();
  }, []);

  const handleOpenNotificationMenu = (event) =>
    setOpenNotificationMenu(event.currentTarget);
  const handleCloseNotificationMenu = () => setOpenNotificationMenu(false);

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      data-testid="notification-menu"
      anchorEl={openNotificationMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      open={Boolean(openNotificationMenu)}
      onClose={handleCloseNotificationMenu}
      sx={{ mt: 1 }}
      slotProps={{
        paper: {
          style: {
            width: 500,
            maxHeight: 600,
            padding: 2,
          },
        },
      }}>
      <Notifications
        notifications={notifications}
        setNotifications={setNotifications}
        setNumNewNotifications={setNumNewNotifications}
        notificationsPreferences={notificationsPreferences}
        setOpenNotificationMenu={setOpenNotificationMenu}
        notificationFilterOptions={notificationFilterOptions}
        // notificationsPreferences={notificationsPreferences}
      />
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = () => ({
    color: 'white !important',
  });

  return (
    <AppBar
      data-testid="app-bar"
      position="fixed"
      sx={(theme) =>
        navbar(theme, { transparentNavbar, absolute, light, darkMode })
      } // defines the color/style of the navbar
    >
      <Toolbar sx={(theme) => navbarContainer(theme)} data-testid="mui-Toolbar">
        <MDBox display="flex" alignItems="center">
          <IconButton size="large" color="inherit" onClick={onOpenDrawer}>
            <MenuIcon sx={iconsStyle} />
          </IconButton>
          <MDBox
            component={NavLink}
            to="/home"
            display="flex"
            alignItems="center"
            sx={{ marginLeft: '20px', marginRight: '10px' }}>
            <MDBox component="img" src={brand} alt="Brand" width="3rem" />
            <MDTypography
              component="h1"
              color="white"
              variant="button"
              fontWeight="bold">
              {COMPANY_NAME}
            </MDTypography>
          </MDBox>

          <Tooltip
            title={intl.get('shared_current_selected_organization', {
              organization: currentOrganization,
            })}>
            <Chip
              label={currentOrganization}
              icon={<CorporateFareIcon />}
              color="primary"
              variant="outlined"
              sx={{
                padding: '6px 10px', // Increase overall padding
                height: 'auto', // Allow height to adjust to content
                '& .MuiChip-icon': {
                  marginLeft: '6px', // Add space between icon and label
                  marginRight: '3px', // Add space between icon and label
                },
                '& .MuiChip-label': {
                  paddingLeft: '6px', // Add space between icon and text
                  paddingRight: '6px', // Add space after text
                },
              }}
            />
          </Tooltip>
        </MDBox>

        {doesUserHaveHistoryPrivilege() && (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox color={light ? 'white' : 'inherit'}>
              <IconButton
                data-testid="navbar-notification-button"
                size="medium"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                variant="contained"
                onClick={handleOpenNotificationMenu}>
                <MDBadge
                  badgeContent={numNewNotifications}
                  color="error"
                  size="xs"
                  rounded={numNewNotifications >= 10}
                  circular={numNewNotifications < 10}>
                  <Icon sx={iconsStyle}>notifications</Icon>
                </MDBadge>
              </IconButton>

              {renderMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  brand: PropTypes.string,
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  onOpenDrawer: PropTypes.func,
};

export default DashboardNavbar;
